import { useParams, useNavigate } from 'react-router-dom';
import { Recipe } from '@/types/api';
import { RecipeDetail } from '@/components/recipes/RecipeDetail';
import { Button } from '@/components/ui/button';
import { useRecipe } from '@/hooks';
import { useFavoritesStore } from '@/stores/favoritesStore';
import { AlertCircle, ArrowLeft } from 'lucide-react';

export function RecipeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Favorites store
  const { toggleFavorite, isFavorite, setRating, getRating } = useFavoritesStore();

  const {
    data: recipe,
    isLoading,
    isError,
    error,
    refetch,
  } = useRecipe(id!, {
    enabled: !!id,
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleFavoriteToggle = (recipe: Recipe) => {
    toggleFavorite(recipe.id);
  };

  const handleRatingChange = (recipe: Recipe, rating: number) => {
    setRating(recipe.id, rating);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex items-center gap-4">
            <div className="h-9 w-20 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="aspect-[16/9] md:aspect-[2/1] bg-gray-200 animate-pulse" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-lg p-6">
                <div className="h-6 bg-gray-200 rounded animate-pulse mb-4" />
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6">
                <div className="h-6 bg-gray-200 rounded animate-pulse mb-4" />
                <div className="space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-4 bg-gray-200 rounded animate-pulse" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !recipe) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="flex justify-start mb-6">
            <Button
              variant="outline"
              size="sm"
              onClick={handleBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver
            </Button>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              No se pudo cargar la receta
            </h1>
            <p className="text-gray-600 mb-6">
              {error?.message || 'La receta que buscas no existe o no se pudo cargar.'}
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button variant="outline" onClick={() => navigate('/recipes')}>
                Ver todas las recetas
              </Button>
              <Button onClick={() => refetch()}>Intentar de nuevo</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <RecipeDetail
        recipe={recipe}
        onBack={handleBack}
        onFavoriteToggle={handleFavoriteToggle}
        onRatingChange={handleRatingChange}
        isFavorite={isFavorite(recipe.id)}
        userRating={getRating(recipe.id)}
        averageRating={4.2}
        totalRatings={127}
      />
    </div>
  );
}
